import type { Branch, Me } from '@/features/auth/types';

/** What a recovery settled on, once `me` was reloaded. */
export type BranchRecoveryOutcome =
  /** The selected branch was kept or reset; refetch the cached queries. */
  | { kind: 'refetch'; switchedTo: Branch | 'all' | null }
  /** The role no longer holds any branch: show the no-access state. */
  | { kind: 'no_branches' };

export interface BranchRecoveryDeps {
  /** Re-reads `me`; null when it could not be read (the session handles that). */
  reloadMe: () => Promise<Me | null>;
  /** The topbar selection right now: a branch id, or null for "All". */
  currentSelection: () => string | null;
  select: (branchId: string | 'all') => void;
  settled: (outcome: BranchRecoveryOutcome) => void;
  now?: () => number;
  /** Rejections arriving this soon after a recovery are the same burst. */
  cooldownMs?: number;
}

/** Long enough to cover the refetch the recovery itself starts. */
export const BRANCH_RECOVERY_COOLDOWN_MS = 10_000;

/**
 * RL-1176: when the API refuses the X-Branch-Id (403 BRANCH_NOT_PERMITTED or
 * 400 BRANCH_ID_INVALID) reload `me` once, reset the branch if the role no
 * longer holds it (first branch, else "All" where allowed), then refetch.
 * Concurrent rejections share one reload, and rejections right after a
 * recovery (the cooldown) start none, so there is never a retry loop.
 */
export function createBranchRecovery(deps: BranchRecoveryDeps) {
  const now = deps.now ?? Date.now;
  const cooldownMs = deps.cooldownMs ?? BRANCH_RECOVERY_COOLDOWN_MS;
  let inFlight: Promise<void> | null = null;
  let settledAt = Number.NEGATIVE_INFINITY;

  async function run(): Promise<void> {
    // Read before the reload: once the new `me` renders, the provider already
    // falls back to another branch.
    const current = deps.currentSelection();
    const me = await deps.reloadMe();
    if (!me) return;
    if (me.branches.length === 0 && !me.canViewAllBranches) {
      deps.settled({ kind: 'no_branches' });
      return;
    }
    const stillHeld =
      current === null
        ? me.canViewAllBranches
        : me.branches.some((b) => b.id === current);
    if (stillHeld) {
      deps.settled({ kind: 'refetch', switchedTo: null });
      return;
    }
    const first = me.branches[0];
    deps.select(first ? first.id : 'all');
    deps.settled({ kind: 'refetch', switchedTo: first ?? 'all' });
  }

  return {
    /** Starts (or joins) a recovery; resolves when it settled. */
    handle(): Promise<void> {
      if (inFlight) return inFlight;
      if (now() - settledAt < cooldownMs) return Promise.resolve();
      inFlight = run().finally(() => {
        settledAt = now();
        inFlight = null;
      });
      return inFlight;
    },
  };
}
